import type {ExtractPropsWithoutRef} from '@/types';

import styled from '@emotion/styled';
import {RatingSnippet as UIRatingSnippet} from '@travel-frontend/ui-components';
import {useTranslation} from 'react-i18next';

import {Badge} from '@/components/RatingSnippet/components/Badge';
import {ActionLink} from '@/components/RatingSummary/components/ActionLink';

type Props = Omit<
  ExtractPropsWithoutRef<typeof UIRatingSnippet>,
  'label' | 'badge' | 'description'
> & {
  score: number;
  scoreDescription: string;
  reviewCount: number;
  recommendationRate?: number;
  compact?: boolean;
  onClickReviewCount?: () => void;
};

export const RatingSnippet = styled(
  ({
    score,
    scoreDescription,
    reviewCount,
    recommendationRate,
    contained = false,
    fullWidth = false,
    compact = false,
    onClickReviewCount,
    ...restProps
  }: Props) => {
    const {t} = useTranslation('general');

    return (
      <UIRatingSnippet
        {...restProps}
        badge={<Badge score={score} />}
        // @ts-expect-error toolkit type is overly strict, `Element` works just fine (plus we're going to throw this component away soon)
        label={
          <>
            {scoreDescription}
            {reviewCount >= 0 && (
              <>
                &nbsp;
                {typeof onClickReviewCount === 'function' ? (
                  <ActionLink onClick={onClickReviewCount}>
                    {t('ratingSummary.reviewCount', {
                      count: reviewCount,
                      context: compact ? 'compact' : '',
                    })}
                  </ActionLink>
                ) : (
                  t('ratingSummary.reviewCount', {
                    count: reviewCount,
                    context: compact ? 'compact' : '',
                  })
                )}
              </>
            )}
          </>
        }
        description={
          recommendationRate
            ? t('rating.snippet.description', {
                recommendationRate,
              })
            : undefined
        }
        contained={contained}
        fullWidth={fullWidth}
      />
    );
  }
)``;
